import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

// Tour step definition
export interface TourStep {
  id: string;
  target: string; // CSS selector or menu item ID
  title: string;
  description: string;
  position: 'left' | 'right' | 'top' | 'bottom';
}

// Tour steps configuration
export const TOUR_STEPS: TourStep[] = [
  {
    id: 'settings',
    target: 'menu-item-settings',
    title: 'Settings',
    description: 'Configure your store settings, shipping policies, and listing preferences.',
    position: 'right',
  },
  {
    id: 'store-management',
    target: 'menu-item-store-management',
    title: 'Store Management',
    description: 'Add and manage multiple eBay stores from different marketplaces.',
    position: 'right',
  },
  {
    id: 'products',
    target: 'menu-item-products',
    title: 'Products',
    description: 'View and manage your synced eBay listings and inventory.',
    position: 'right',
  },
  {
    id: 'drafts',
    target: 'menu-item-product-import',
    title: 'Product Import',
    description: 'Create new listings from Amazon products with AI-powered optimization.',
    position: 'right',
  },
  {
    id: 'orders',
    target: 'menu-item-orders',
    title: 'Orders',
    description: 'Track and manage your customer orders in real-time.',
    position: 'right',
  },
  {
    id: 'subscription',
    target: 'menu-item-subscription',
    title: 'Subscription',
    description: 'Manage your subscription plan and billing details.',
    position: 'right',
  },
];

interface TourContextType {
  isActive: boolean;
  currentStep: number;
  currentTourStep: TourStep | null;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  endTour: () => void;
  goToStep: (stepIndex: number) => void;
  totalSteps: number;
}

const TourContext = createContext<TourContextType>({
  isActive: false,
  currentStep: 0,
  currentTourStep: null,
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  endTour: () => {},
  goToStep: () => {},
  totalSteps: TOUR_STEPS.length,
});

export const useTour = () => useContext(TourContext);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // End tour when reaching the last step
      setIsActive(false);
      setCurrentStep(0);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    // Save to localStorage that user has completed the tour
    localStorage.setItem('tourCompleted', 'true');
  }, []);

  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < TOUR_STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  const currentTourStep = isActive ? TOUR_STEPS[currentStep] : null;

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        currentTourStep,
        startTour,
        nextStep,
        prevStep,
        endTour,
        goToStep,
        totalSteps: TOUR_STEPS.length,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
